import * as faceapi from "face-api.js";
import { getFaceBlurDebugBlurAllFaces } from "./consts";
import { logger } from "library/core/utility";

interface FaceBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DetectedFaceBox {
  boundingBox: FaceBox;
  area: number;
}

const logPrefix = "[FaceBlurProcessor]:";

// CDN URL for face-api.js models
const MODELS_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model";

/**
 * FaceBlurProcessor - Real-time face detection and blurring for WebRTC streams
 *
 * Uses face-api.js (TensorFlow.js based) for reliable long-range face detection:
 * - SSD Mobilenet v1 model for better detection of distant/small faces
 * - Adaptive blur (pixelation) based on face size
 */
export class FaceBlurProcessor {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private offscreenCanvas: HTMLCanvasElement;
  private offscreenCtx: CanvasRenderingContext2D;
  private videoElement: HTMLVideoElement | null = null;
  private processedStream: MediaStream | null = null;
  private originalStream: MediaStream | null = null;
  private animationFrameId: number | null = null;
  private enabled: boolean = true;
  private isInitialized: boolean = false;
  private isProcessing: boolean = false;
  private modelsLoaded: boolean = false;
  private lastDetectedFacesBox: DetectedFaceBox[] = [];
  private frameCount: number = 0;
  private lastFpsCheck: number = 0;
  private currentFps: number = 30;
  private readonly targetFps: number = 30;
  // Run detection every N frames for performance (face-api.js is heavier)
  private readonly detectionInterval: number = 3;
  // Adaptive blur: min/max radius based on face size (higher = more pixelated)
  private readonly minBlurRadius: number = 50; // For very small faces
  private readonly maxBlurRadius: number = 100; // For larger faces
  // Faces larger than this % of frame area are considered "selfie distance" and won't be blurred
  // A face taking up 4% of the frame is roughly at arm's length or closer
  private readonly maxFaceAreaToBlur: number = 0.04;
  // Minimum face area to attempt detection (very small faces below this may be noise)
  private readonly minFaceAreaToProcess: number = 0.0001; // 0.01% of frame
  // How far from center a single face can be to still be considered the "broadcaster"
  // 0.3 = face center must be within 30% of frame center on each axis
  private readonly centerThreshold: number = 0.4;
  // Detection options for face-api.js
  private detectionOptions: faceapi.SsdMobilenetv1Options;

  constructor() {
    // Create main canvas for output
    this.canvas = document.createElement("canvas");
    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D context for main canvas");
    }
    this.ctx = ctx;

    // Create offscreen canvas for blur operations
    this.offscreenCanvas = document.createElement("canvas");
    const offscreenCtx = this.offscreenCanvas.getContext("2d");
    if (!offscreenCtx) {
      throw new Error("Failed to get 2D context for offscreen canvas");
    }
    this.offscreenCtx = offscreenCtx;

    // Configure SSD Mobilenet detection options
    // Lower minConfidence = detect more faces including distant/small ones
    this.detectionOptions = new faceapi.SsdMobilenetv1Options({
      minConfidence: 0.3, // threshold for long-range detection
      maxResults: 20, // number of faces to detect
    });

    this.log("FaceBlurProcessor created");
  }

  private log = (...params: any[]) => {
    logger.log(logPrefix, ...params);
  };

  /**
   * Initialize the processor with a video track from getUserMedia
   */
  async initialize(mediaStream: MediaStream): Promise<void> {
    this.log("Initializing with media stream");

    this.originalStream = mediaStream;
    const videoTrack = mediaStream.getVideoTracks()[0];

    if (!videoTrack) {
      throw new Error("No video track found in media stream");
    }

    // Create a video element to draw frames from
    this.videoElement = document.createElement("video");
    this.videoElement.srcObject = mediaStream;
    this.videoElement.muted = true;
    this.videoElement.playsInline = true;
    this.videoElement.autoplay = true;

    // Wait for video to be ready BEFORE setting canvas dimensions
    // This ensures we get the actual video dimensions, not fallback values
    await new Promise<void>((resolve, reject) => {
      if (!this.videoElement) {
        reject(new Error("Video element not created"));
        return;
      }

      const videoEl = this.videoElement;

      // Use canplaythrough for better iOS Safari compatibility
      const onReady = async () => {
        videoEl.removeEventListener("canplaythrough", onReady);
        videoEl.removeEventListener("loadeddata", onReady);

        try {
          await videoEl.play();

          // Wait until video actually has dimensions (iOS Safari timing issue)
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max
          while (
            (videoEl.videoWidth === 0 || videoEl.videoHeight === 0) &&
            attempts < maxAttempts
          ) {
            await new Promise(r => setTimeout(r, 100));
            attempts++;
          }

          if (videoEl.videoWidth === 0 || videoEl.videoHeight === 0) {
            this.log("Warning: Video dimensions still 0 after waiting");
          } else {
            this.log("Video ready with dimensions", {
              videoWidth: videoEl.videoWidth,
              videoHeight: videoEl.videoHeight,
            });
          }

          resolve();
        } catch (e) {
          reject(e);
        }
      };

      // Listen for both events - use whichever fires and video is ready
      videoEl.addEventListener("canplaythrough", onReady);
      videoEl.addEventListener("loadeddata", onReady);

      videoEl.onerror = () => reject(new Error("Failed to load video"));
    });

    // Now get dimensions from the actual video element (which has real dimensions)
    // Fall back to track settings, then to defaults only as last resort
    const settings = videoTrack.getSettings();
    const width = this.videoElement.videoWidth || settings.width || 1280;
    const height = this.videoElement.videoHeight || settings.height || 720;

    // Set canvas dimensions to match actual video dimensions
    this.canvas.width = width;
    this.canvas.height = height;
    this.offscreenCanvas.width = width;
    this.offscreenCanvas.height = height;

    this.log("Canvas dimensions set", { width, height });

    // Load face-api.js models
    try {
      await this.loadModels();
      this.log("Face-api.js models loaded");
    } catch (error) {
      this.log("Face-api.js models failed to load", error);
      throw new Error("Face detection models could not be loaded");
    }

    // Create the processed output stream
    this.processedStream = this.canvas.captureStream(this.targetFps);

    // Copy audio track from original stream to processed stream
    const audioTrack = mediaStream.getAudioTracks()[0];
    if (audioTrack) {
      this.processedStream.addTrack(audioTrack);
    }

    this.isInitialized = true;

    // Debug: Check processed stream tracks
    const videoTracks = this.processedStream?.getVideoTracks() || [];
    const audioTracks = this.processedStream?.getAudioTracks() || [];
    this.log("Initialization complete", {
      width,
      height,
      processedStreamTracks: {
        video: videoTracks.length,
        audio: audioTracks.length,
        videoTrackState: videoTracks[0]?.readyState,
        videoTrackEnabled: videoTracks[0]?.enabled,
      },
    });

    // Start the processing loop
    this.startProcessingLoop();
  }

  /**
   * Load face-api.js models from CDN
   */
  private async loadModels(): Promise<void> {
    this.log("Loading face-api.js models from CDN...");

    // Load SSD Mobilenet v1 model - best for detecting faces at various distances
    await faceapi.nets.ssdMobilenetv1.loadFromUri(MODELS_URL);

    this.modelsLoaded = true;
    this.log("Face-api.js SSD Mobilenet v1 model loaded");
  }

  /**
   * Detect faces in the current canvas frame
   */
  private async detectFaces(): Promise<void> {
    if (!this.modelsLoaded) return;

    try {
      // Detect all faces using SSD Mobilenet v1
      const detections = await faceapi.detectAllFaces(
        this.canvas,
        this.detectionOptions
      );

      const canvasWidth = this.canvas.width;
      const canvasHeight = this.canvas.height;

      this.lastDetectedFacesBox = detections.map(detection => {
        const box = detection.box;
        // Calculate normalized area (0-1 range)
        const normalizedWidth = box.width / canvasWidth;
        const normalizedHeight = box.height / canvasHeight;
        const area = normalizedWidth * normalizedHeight;

        return {
          boundingBox: {
            x: box.x,
            y: box.y,
            width: box.width,
            height: box.height,
          },
          area,
        };
      });

      // Sort by area descending (largest face first)
      this.lastDetectedFacesBox.sort((a, b) => b.area - a.area);
    } catch (error) {
      this.log("Face detection error:", error);
    }
  }

  /**
   * Start the video processing loop
   */
  private startProcessingLoop(): void {
    if (this.animationFrameId !== null) {
      return;
    }

    this.lastFpsCheck = performance.now();
    this.frameCount = 0;

    const processFrame = async () => {
      if (!this.isInitialized || !this.videoElement) {
        this.animationFrameId = requestAnimationFrame(processFrame);
        return;
      }

      // FPS throttling
      const now = performance.now();
      const elapsed = now - this.lastFpsCheck;

      if (elapsed >= 1000) {
        this.currentFps = Math.round((this.frameCount * 1000) / elapsed);
        this.frameCount = 0;
        this.lastFpsCheck = now;
      }

      // Process the current frame
      await this.processCurrentFrame();
      this.frameCount++;

      this.animationFrameId = requestAnimationFrame(processFrame);
    };

    this.animationFrameId = requestAnimationFrame(processFrame);
    this.log("Processing loop started");
  }

  /**
   * Process a single video frame
   */
  private async processCurrentFrame(): Promise<void> {
    if (!this.videoElement || this.isProcessing) {
      return;
    }

    // Skip processing if video doesn't have valid dimensions yet (iOS Safari timing issue)
    if (
      this.videoElement.videoWidth === 0 ||
      this.videoElement.videoHeight === 0
    ) {
      return;
    }

    this.isProcessing = true;

    try {
      // Draw the current video frame to the main canvas
      this.ctx.drawImage(
        this.videoElement,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );

      // Debug: Log periodically to confirm frames are being processed
      if (this.frameCount % 60 === 0) {
        this.log("Processing frames", {
          frameCount: this.frameCount,
          fps: this.currentFps,
          facesDetected: this.lastDetectedFacesBox.length,
          videoSize: `${this.videoElement.videoWidth}x${this.videoElement.videoHeight}`,
        });
      }

      // Run face detection periodically (every N frames for performance)
      if (this.frameCount % this.detectionInterval === 0 && this.modelsLoaded) {
        await this.detectFaces();
      }

      // Apply blur to detected faces
      if (this.enabled && this.lastDetectedFacesBox.length > 0) {
        this.applyFaceBlur();
      }
    } catch (error) {
      this.log("Error processing frame:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Check if a face is near the center of the frame
   */
  private isFaceNearCenter(face: DetectedFaceBox): boolean {
    const { boundingBox } = face;
    // Calculate face center as normalized coordinates (0-1)
    const faceCenterX =
      (boundingBox.x + boundingBox.width / 2) / this.canvas.width;
    const faceCenterY =
      (boundingBox.y + boundingBox.height / 2) / this.canvas.height;

    // Check if face center is within threshold of frame center (0.5, 0.5)
    const distFromCenterX = Math.abs(faceCenterX - 0.5);
    const distFromCenterY = Math.abs(faceCenterY - 0.5);

    return (
      distFromCenterX <= this.centerThreshold &&
      distFromCenterY <= this.centerThreshold
    );
  }

  /**
   * Apply blur to detected faces
   */
  private applyFaceBlur(): void {
    const debugBlurAll = getFaceBlurDebugBlurAllFaces();

    for (const face of this.lastDetectedFacesBox) {
      // Skip very tiny faces that might be noise
      if (face.area < this.minFaceAreaToProcess) {
        continue;
      }

      // Skip faces at selfie distance (too large = too close to camera), unless debug mode
      if (!debugBlurAll && face.area > this.maxFaceAreaToBlur) {
        continue;
      }

      // Special case: If only 1 face that would be blurred and it's near center, assume it's the broadcaster
      // This only applies to faces that passed the area check above (not too close)
      if (!debugBlurAll && this.lastDetectedFacesBox.length === 1) {
        if (this.isFaceNearCenter(face)) {
          // Single centered face at distance = broadcaster, don't blur
          continue;
        }
      }

      this.applyBoxBlur(face, face.area);
    }

    // Debug: Draw boxes around ALL detected faces when debug mode is on
    if (debugBlurAll) {
      this.drawDebugFaceBoxes();
    }
  }

  /**
   * Apply blur to a face detection box with adaptive radius
   */
  private applyBoxBlur(face: DetectedFaceBox, area: number): void {
    const { boundingBox } = face;

    // Coordinates are already in pixels from face-api.js
    const x = Math.floor(boundingBox.x);
    const y = Math.floor(boundingBox.y);
    const width = Math.ceil(boundingBox.width);
    const height = Math.ceil(boundingBox.height);

    // Add slight padding around the face bounding box
    const paddingFactor = 0.15; // Add 15% padding on each side
    const paddingX = width * paddingFactor;
    const paddingY = height * paddingFactor;
    const blurX = Math.max(0, x - paddingX);
    const blurY = Math.max(0, y - paddingY);
    const blurWidth = Math.min(this.canvas.width - blurX, width + paddingX * 2);
    const blurHeight = Math.min(
      this.canvas.height - blurY,
      height + paddingY * 2
    );

    // Calculate adaptive blur radius based on face size
    const blurRadius = this.calculateAdaptiveBlurRadius(area);

    this.applyEllipseBlurWithRadius(
      blurX,
      blurY,
      blurWidth,
      blurHeight,
      blurRadius
    );
  }

  /**
   * Calculate adaptive blur radius based on face area
   * Smaller faces get less blur to avoid looking like blobs
   */
  private calculateAdaptiveBlurRadius(area: number): number {
    // Map face area to blur radius
    // Very small faces (0.01% - 0.5%): minBlurRadius
    // Medium faces (0.5% - 2%): scale between min and max
    // Large faces (2% - 4%): maxBlurRadius
    const minArea = 0.001; // 0.1% of frame
    const maxArea = 0.02; // 2% of frame

    if (area <= minArea) {
      return this.minBlurRadius;
    }
    if (area >= maxArea) {
      return this.maxBlurRadius;
    }

    // Linear interpolation between min and max blur radius
    const t = (area - minArea) / (maxArea - minArea);
    return Math.round(
      this.minBlurRadius + t * (this.maxBlurRadius - this.minBlurRadius)
    );
  }

  /**
   * Draw debug boxes around all detected faces
   * Yellow = selfie distance / too close (won't be blurred)
   * Green = broadcaster (single face at distance, centered - won't be blurred)
   * Red = background face (will be blurred)
   */
  private drawDebugFaceBoxes(): void {
    for (let i = 0; i < this.lastDetectedFacesBox.length; i++) {
      const face = this.lastDetectedFacesBox[i];
      const { boundingBox } = face;
      const isSelfieDistance = face.area > this.maxFaceAreaToBlur;
      const isNearCenter = this.isFaceNearCenter(face);
      const blurRadius = this.calculateAdaptiveBlurRadius(face.area);

      // Broadcaster = single face, NOT selfie distance, AND near center
      const isBroadcaster =
        this.lastDetectedFacesBox.length === 1 &&
        !isSelfieDistance &&
        isNearCenter;

      // Coordinates are already in pixels
      const x = boundingBox.x;
      const y = boundingBox.y;
      const w = boundingBox.width;
      const h = boundingBox.height;

      // Determine status and color
      let color: string;
      let status: string;

      if (isSelfieDistance) {
        color = "yellow";
        status = "CLOSE";
      } else if (isBroadcaster) {
        color = "lime";
        status = "BROADCASTER";
      } else {
        color = "red";
        status = "BLUR";
      }

      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x, y, w, h);

      // Draw label with face index, area, blur radius, center status, and blur status
      this.ctx.fillStyle = color;
      this.ctx.font = "12px Arial";
      const centerLabel = isNearCenter ? "C" : "";
      this.ctx.fillText(
        `#${i} ${status} ${centerLabel} ${(face.area * 100).toFixed(
          2
        )}% r=${blurRadius}`,
        x,
        y - 4
      );
    }
  }

  /**
   * Apply ellipse blur with adaptive radius
   */
  private applyEllipseBlurWithRadius(
    x: number,
    y: number,
    width: number,
    height: number,
    blurRadius: number
  ): void {
    if (width <= 0 || height <= 0) return;

    this.ctx.save();

    // Create ellipse clipping path
    this.ctx.beginPath();
    this.ctx.ellipse(
      x + width / 2,
      y + height / 2,
      width / 2,
      height / 2,
      0,
      0,
      Math.PI * 2
    );
    this.ctx.clip();

    // Apply blur with specified radius
    this.applyBlurEffectWithRadius(x, y, width, height, blurRadius);

    this.ctx.restore();
  }

  /**
   * Apply the actual blur effect to a region with specified radius
   * Uses pixelation for reliable face obscuring
   */
  private applyBlurEffectWithRadius(
    x: number,
    y: number,
    width: number,
    height: number,
    blurRadius: number
  ): void {
    if (width <= 0 || height <= 0) return;

    // Use pixelation - more reliable than CSS filter blur
    // Higher blur radius = fewer pixels = more obscured
    const pixelSize = Math.max(8, Math.round(blurRadius / 3));

    // Calculate tiny size for pixelation
    const tinyWidth = Math.max(2, Math.ceil(width / pixelSize));
    const tinyHeight = Math.max(2, Math.ceil(height / pixelSize));

    // Ensure offscreen canvas is big enough
    if (
      this.offscreenCanvas.width < width ||
      this.offscreenCanvas.height < height
    ) {
      this.offscreenCanvas.width = Math.max(this.offscreenCanvas.width, width);
      this.offscreenCanvas.height = Math.max(
        this.offscreenCanvas.height,
        height
      );
    }

    // Step 1: Draw the face region scaled down to tiny size (pixelates it)
    this.offscreenCtx.drawImage(
      this.canvas,
      x,
      y,
      width,
      height,
      0,
      0,
      tinyWidth,
      tinyHeight
    );

    // Step 2: Draw back scaled up with no smoothing (blocky pixels)
    this.ctx.imageSmoothingEnabled = false;
    this.ctx.drawImage(
      this.offscreenCanvas,
      0,
      0,
      tinyWidth,
      tinyHeight,
      x,
      y,
      width,
      height
    );
    this.ctx.imageSmoothingEnabled = true;
  }

  /**
   * Get the processed media stream
   */
  getProcessedStream(): MediaStream | null {
    return this.processedStream;
  }

  /**
   * Get the original unprocessed stream
   */
  getOriginalStream(): MediaStream | null {
    return this.originalStream;
  }

  /**
   * Enable or disable face blur processing
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.log(`Face blur ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Check if face blur is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Check if the processor is initialized and ready
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current processing FPS
   */
  getCurrentFps(): number {
    return this.currentFps;
  }

  /**
   * Get the number of detected faces
   */
  getDetectedFaceCount(): number {
    return this.lastDetectedFacesBox.length;
  }

  /**
   * Update canvas dimensions when resolution changes
   */
  async updateDimensions(width: number, height: number): Promise<void> {
    this.canvas.width = width;
    this.canvas.height = height;
    this.offscreenCanvas.width = width;
    this.offscreenCanvas.height = height;
    this.log("Dimensions updated", {
      width,
      height,
    });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.log("Destroying FaceBlurProcessor");

    // Stop the processing loop
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Stop video element
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    // Stop processed stream tracks
    if (this.processedStream) {
      this.processedStream.getTracks().forEach(track => {
        // Don't stop audio track as it comes from original stream
        if (track.kind === "video") {
          track.stop();
        }
      });
      this.processedStream = null;
    }

    this.originalStream = null;
    this.isInitialized = false;
    this.lastDetectedFacesBox = [];

    this.log("FaceBlurProcessor destroyed");
  }
}

// Export singleton-like factory
let processorInstance: FaceBlurProcessor | null = null;

export const getFaceBlurProcessor = (): FaceBlurProcessor => {
  if (!processorInstance) {
    processorInstance = new FaceBlurProcessor();
  }
  return processorInstance;
};

export const destroyFaceBlurProcessor = (): void => {
  if (processorInstance) {
    processorInstance.destroy();
    processorInstance = null;
  }
};

export default FaceBlurProcessor;
